import AuthContext, { rolesMap } from 'context/authContext';
import { useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import paths from 'routes/paths';
import UserForm from './UserForm';
import { useUserRole, useUpdateUserRole, useUpdateUser } from 'hooks/useUsers';
import { authHelpers } from 'lib/supabase';

export default function EditUser() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);

  // Use Supabase hooks
  const { userRole, isLoading: isLoadingUserRole } = useUserRole(id);
  const { updateUserRoleAsync, isUpdating: isUpdatingRole } =
    useUpdateUserRole();
  const { updateUserAsync, isUpdating: isUpdatingUser } = useUpdateUser();

  const onSubmit = async values => {
    try {
      if (!userRole) {
        toast.error('User data not loaded');
        return;
      }

      // If changing to super admin, verify the current user's password first
      if (values.superAdmin && values.adminPassword) {
        const { verified, error } = await authHelpers.verifyPassword(
          user.email,
          values.adminPassword
        );

        if (!verified || error) {
          toast.error('Invalid password. Please enter your correct password.');
          return;
        }
      }

      // Filter out parent-level permissions, keep only child permissions
      const childPermissions = {};
      if (!values.superAdmin && values.permissions) {
        Object.keys(values.permissions).forEach(key => {
          // Check if this is a child permission (has specific actions as properties)
          if (
            values.permissions[key] &&
            typeof values.permissions[key] === 'object' &&
            !key.includes('Parent')
          ) {
            childPermissions[key] = values.permissions[key];
          }
        });
      }

      // Always add dashboard read permission for all users
      childPermissions.dashboard = { read: true };

      // Update user basic info
      await updateUserAsync({
        id: userRole.user_id,
        data: {
          first_name: values.firstName,
          last_name: values.lastName
        }
      });

      // Determine role ID based on selected role
      let roleId = rolesMap.staff.id;
      if (values.superAdmin) {
        roleId = rolesMap.admin.id;
      } else if (values.groundsTeamManager) {
        roleId = rolesMap.groundsTeamManager.id;
      } else if (values.groundTeam) {
        roleId = rolesMap.groundTeam.id;
      }

      // Update user role and permissions
      await updateUserRoleAsync({
        id: id,
        data: {
          branch_id: values.branch,
          role_id: roleId,
          permissions: values.superAdmin ? null : childPermissions
        }
      });

      // Navigate back to users list on success
      navigate(paths.userRolesSetting);
    } catch (error) {
      console.error('Update user error:', error);
      // Error toast is handled in the hooks
    }
  };

  return (
    <UserForm
      isEdit
      loading={isLoadingUserRole || isUpdatingRole || isUpdatingUser}
      onSubmit={onSubmit}
      initValues={userRole}
    />
  );
}
